import React, { useCallback, useEffect, useRef, useState } from 'react';
import AlertModal from './AlertModal';
import LoaderOverlay from './LoaderOverlay';
import '../styles/formStyles.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

function AdminPreClientsImport() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importedCompanyName, setImportedCompanyName] = useState('');
  const [alert, setAlert] = useState({ open: false, type: 'info', message: '' });
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token');

  const showAlert = (type, message) => {
    setAlert({ open: true, type, message });
  };

  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const response = await fetch(`${API_URL}/admin/lojistas/ativos`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao carregar os lojistas ativos.');
      }

      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      setCompanies([]);
      showAlert('error', error.message || 'Erro ao carregar os lojistas ativos.');
    } finally {
      setLoadingCompanies(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    if (!selectedCompanyId) {
      showAlert('warning', 'Selecione um lojista antes de importar os contatos.');
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension !== 'csv') {
      showAlert('error', 'Selecione um arquivo com extensão .csv.');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showAlert('error', 'O arquivo não pode ultrapassar 10 MB.');
      return;
    }

    setSelectedFile(file);
  };

  const handleImportConfirmation = async () => {
    if (!selectedFile || !selectedCompanyId) return;

    const formData = new FormData();
    formData.append('file', selectedFile);
    setImporting(true);
    setImportResult(null);

    try {
      const response = await fetch(
        `${API_URL}/companies/${selectedCompanyId}/pre-clients/import`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
          body: formData,
        }
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao importar os contatos.');
      }

      setImportResult(data);
      setImportedCompanyName(
        companies.find((company) => String(company.id) === String(selectedCompanyId))?.nome || ''
      );
    } catch (error) {
      showAlert('error', error.message || 'Erro ao importar os contatos.');
    } finally {
      setImporting(false);
      setSelectedFile(null);
    }
  };

  const cancelImport = () => {
    if (!importing) {
      setSelectedFile(null);
    }
  };

  const summary = importResult?.summary || {};
  const errors = Array.isArray(importResult?.errors) ? importResult.errors : [];

  return (
    <section className="dashboard-section">
      <LoaderOverlay
        isLoading={loadingCompanies || importing}
        message={loadingCompanies ? 'Carregando lojistas...' : 'Importando contatos...'}
      />
      <h3>Importação de contatos</h3>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
        }}
      >
        <div className="filter-group" style={{ minWidth: '280px' }}>
          <label htmlFor="preClientsCompany">Lojista:</label>
          <select
            id="preClientsCompany"
            className="input-style"
            value={selectedCompanyId}
            onChange={(event) => setSelectedCompanyId(event.target.value)}
            disabled={loadingCompanies || importing || Boolean(selectedFile)}
          >
            <option value="">
              {loadingCompanies ? 'Carregando lojistas...' : 'Selecione um lojista'}
            </option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.nome}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="button-style"
          onClick={() => fileInputRef.current?.click()}
          disabled={importing || loadingCompanies || !selectedCompanyId}
        >
          {importing ? 'Importando...' : 'Importar contatos'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>

      {selectedFile && !importing && (
        <div className="modal-overlay">
          <div className="modal-content small-modal">
            <h3>Confirmar importação</h3>
            <p>
              Deseja importar o arquivo <strong>{selectedFile.name}</strong> para o lojista selecionado?
            </p>
            <p style={{ color: '#666' }}>
              O arquivo possui {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB.
            </p>
            <div className="modal-actions">
              <button type="button" className="button-style" onClick={handleImportConfirmation}>
                Confirmar importação
              </button>
              <button type="button" className="button-style cancel" onClick={cancelImport}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {companies.length === 0 && !loadingCompanies && (
        <p>Nenhum lojista ativo encontrado.</p>
      )}

      {importResult && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '760px', maxHeight: '90vh', overflowY: 'auto' }}>
            <button
              type="button"
              className="modal-close-button"
              onClick={() => {
                setImportResult(null);
                setImportedCompanyName('');
              }}
              aria-label="Fechar resultado da importação"
            >
              &times;
            </button>
            <h3>Importação concluída</h3>
            <p>
              <strong>Lojista:</strong> {importedCompanyName || selectedCompanyId}
            </p>
            <p><strong>Total:</strong> {summary.total ?? 0}</p>
            <p><strong>Válidos:</strong> {summary.valid ?? 0}</p>
            <p><strong>Inválidos:</strong> {summary.invalid ?? 0}</p>
            <p><strong>Atualizações:</strong> {summary.updates ?? 0}</p>
            <p><strong>Novos cadastrados:</strong> {summary.registered ?? 0}</p>
            <p><strong>ID da importação:</strong> {summary.import_id ?? 'N/A'}</p>

            {errors.length > 0 && (
              <>
                <h4>Erros encontrados</h4>
                <div style={{ overflowX: 'auto', maxHeight: '280px' }}>
                  <table className="dashboard-table">
                    <thead>
                      <tr>
                        <th>Linha</th>
                        <th>Campo</th>
                        <th>Valor</th>
                        <th>Mensagem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {errors.map((error, index) => (
                        <tr key={`${error.line || 'error'}-${index}`}>
                          <td>{error.line ?? ''}</td>
                          <td>{error.field ?? ''}</td>
                          <td>{error.value ?? ''}</td>
                          <td>{error.message ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="button-style"
                onClick={() => {
                  setImportResult(null);
                  setImportedCompanyName('');
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertModal
        open={alert.open}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert((previous) => ({ ...previous, open: false }))}
      />
    </section>
  );
}

export default AdminPreClientsImport;
